import { ComponentType, Fragment, ReactNode, useEffect, useRef } from 'react';

import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import Box from '@mui/material/Box';
import { SerializedEditorState } from 'lexical';

import RichTextEditorSkeleton from '../../Skeletons/ParagraphSkeleton';
import AutoLinkPlugin from '../plugins/AutoLinkPlugin';
import MergeTagPlugin from '../plugins/MergeTagPlugin';
import PlaceholderPlugin from '../plugins/PlaceholderPlugin';
import ToolbarPlugin from '../plugins/ToolbarPlugin';
import './styles.css';

/**
 * Props for the RichTextEditor component.
 *
 * @interface RichTextEditorProps
 */
export interface RichTextEditorProps {
  /**
   * The initial serialized state of the editor.
   *
   * @type {SerializedEditorState}
   */
  initialState?: SerializedEditorState;

  /**
   * The placeholder text shown when the editor is empty.
   *
   * @type {string}
   */
  placeholder?: string;

  /**
   * The merge tags available for insertion into the editor.
   *
   * @type {MergeTags}
   */
  mergeTags?: MergeTags;

  /**
   * Optional wrapper component for the content editable area.
   *
   * @type {ComponentType<{ children: ReactNode }>}
   */
  contentEditableWrapper?: ComponentType<{ children: ReactNode }>;

  /**
   * Optional toolbar button components.
   *
   * @type {ReactNode}
   */
  toolbarButtons?: ReactNode;

  /**
   * Callback triggered when the editor state changes.
   *
   * @param {SerializedEditorState} state - The new editor state.
   * @returns {void}
   */
  onChange?: (state: SerializedEditorState) => void;

  /**
   * Whether the editor is loading and should display a skeleton.
   *
   * @type {boolean}
   */
  isLoading?: boolean;
}

export function RichTextEditor({
  initialState,
  placeholder = 'Enter some text...',
  mergeTags,
  contentEditableWrapper,
  toolbarButtons,
  onChange,
  isLoading,
}: RichTextEditorProps) {
  const [editor] = useLexicalComposerContext();
  const hasInitialized = useRef(false);
  // const theme = useTheme();

  // optional wrapper for ContentEditable
  const ContentEditableWrapper = contentEditableWrapper ?? Fragment;

  // hydrate the editor only once
  useEffect(() => {
    if (!hasInitialized.current && initialState) {
      const parsed = editor.parseEditorState(initialState);
      editor.update(() => {
        editor.setEditorState(parsed);
      });
      hasInitialized.current = true;
    }
  }, [initialState, editor]);

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          zIndex: (theme) => theme.zIndex.drawer - 1,
          position: 'sticky',
          top: 0,
          mb: 1,
          px: 1,
          py: 1,
        }}
      >
        <ToolbarPlugin editorType="rich">{toolbarButtons}</ToolbarPlugin>
      </Box>
      <RichTextPlugin
        contentEditable={
          <ContentEditableWrapper>
            {!isLoading && <ContentEditable className="content-editable" />}
            {isLoading && <RichTextEditorSkeleton paragraphs={4} />}
          </ContentEditableWrapper>
        }
        aria-placeholder={'Enter some text...'}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <PlaceholderPlugin placeholder={placeholder} />
      <HistoryPlugin />
      <TabIndentationPlugin maxIndent={7} />
      <ListPlugin />
      <CheckListPlugin />
      <AutoLinkPlugin />
      {mergeTags && <MergeTagPlugin mergeTags={mergeTags} />}
      {onChange && (
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(editorState) => {
            editorState.read(() => {
              const json = editorState.toJSON();
              onChange(json);
            });
          }}
        />
      )}
    </Box>
  );
}

export default RichTextEditor;
